import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ReleasePlan, ReleasePlanDay } from '../../../../../redux/api/fymAPI.types';
import { Panel, PanelBody, PanelHead } from '../../../components/primitives/Panel';
import { SocialPlatform } from '../../types';
import { AddDraftsResult } from './AddDraftsResult';
import { GapBar } from './GapBar';
import { planMessages } from './planMessages';
import { RolloutTimeline } from './RolloutTimeline';
import { usePlanGapActions } from './usePlanGapActions';

interface PushPlanPanelProps {
  accountId: number;
  catalogItemUuid: string;
  /** `GET releases/:uuid/plan?kind=evergreen`: six weekly days from today. */
  plan: ReleasePlan;
  platforms: SocialPlatform[];
  channelChips: React.ReactNode;
  today: string;
}

/**
 * "Push this song" of a released song (design f2; owner answer 8, 2026-10-07): one post a week for
 * 6 weeks. Each week with no post has a tick box; the user unticks a week to leave it out before
 * "Add N push drafts". A missing kit asset becomes a Studio job first.
 */
const PushPlanPanelComponent: React.FC<PushPlanPanelProps> = ({
  accountId,
  catalogItemUuid,
  plan,
  platforms,
  channelChips,
  today,
}) => {
  const intl = useIntl();
  // Empty until the user unticks a week: every week with no post is ticked.
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(() => new Set());
  const gapDays = useMemo(() => plan.days.filter((day) => day.gap), [plan.days]);
  const ticked = useMemo(
    () => new Set(gapDays.filter((day) => !unticked.has(day.key)).map((day) => day.key)),
    [gapDays, unticked]
  );
  const tickedDays = useMemo(() => gapDays.filter((day) => ticked.has(day.key)), [gapDays, ticked]);
  const toggle = useCallback(
    (day: ReleasePlanDay) =>
      setUnticked((current) => {
        const next = new Set(current);
        if (next.has(day.key)) {
          next.delete(day.key);
        } else {
          next.add(day.key);
        }
        return next;
      }),
    []
  );
  const gaps = usePlanGapActions({ accountId, catalogItemUuid, kind: 'evergreen', platforms, days: tickedDays });
  const lines = useMemo(
    () => [intl.formatMessage(planMessages.pushGapBody), ...(gaps.makesLine === null ? [] : [gaps.makesLine])],
    [gaps.makesLine, intl]
  );

  return (
    <Panel as="section" aria-label={intl.formatMessage(planMessages.pushTitle)}>
      <PanelHead
        title={intl.formatMessage(planMessages.pushTitle)}
        subtitle={intl.formatMessage(planMessages.pushSubtitle)}
        right={channelChips}
      />
      <PanelBody stack>
        <RolloutTimeline
          days={plan.days}
          kind="evergreen"
          today={today}
          isAdding={gaps.drafts.isAdding}
          ticked={ticked}
          onToggleTick={toggle}
        />
        {gapDays.length > 0 ? (
          <GapBar
            catalogItemUuid={catalogItemUuid}
            title={intl.formatMessage(planMessages.pushGapTitle, { count: gapDays.length })}
            lines={lines}
            needsReel={gaps.needs.needsReel}
            buttonLabel={intl.formatMessage(planMessages.addPushDrafts, { count: tickedDays.length })}
            canAdd={platforms.length > 0 && tickedDays.length > 0}
            isAdding={gaps.drafts.isAdding}
            onAdd={gaps.addAll}
            result={gaps.drafts.result}
            error={gaps.drafts.error}
          />
        ) : (
          <AddDraftsResult result={gaps.drafts.result} error={gaps.drafts.error} />
        )}
      </PanelBody>
    </Panel>
  );
};

export const PushPlanPanel = TypedMemo(PushPlanPanelComponent);
